import { alpha } from '@mui/material/styles';
import { focusRing } from '../../App/controlStates';

/**
 * The chat/comments design handoff is drawn in purple (#6C2BD9) and Roboto; the
 * app theme is navy with Poppins/Comfortaa. Layout, spacing, radii and the px
 * type scale are kept verbatim - only colour and type are remapped, here, so
 * every part of the panel reads from one map.
 */
export const chatColors = (theme) => ({
    // #6C2BD9 primary / admin / active
    primary: theme.palette.primary.main,
    // #571FB0 hover
    primaryHover: theme.palette.primary.dark,
    // #C0A6EE disabled send
    primaryDisabled: alpha(theme.palette.primary.main, 0.38),
    // #F1EAFC / #F3EEFD tints
    primaryTint: alpha(theme.palette.primary.main, 0.08),
    // #1F6FEB - stays a distinct blue: navy-on-navy would lose the accent
    slideChip: '#1F6FEB',
    // A tag whose slide was deleted keeps its number but drops the blue, so it
    // reads as inert rather than as a slide you can still navigate to.
    deletedChip: '#8A9096',
    // Sender avatars: orange for the room's admins, green for the audience.
    // White initials sit at 5.1:1 on the green but only 3:1 on this orange - a
    // chosen brand colour, below AA for text this size (#C65300 would pass).
    adminAvatar: '#FF6400',
    audienceAvatar: theme.palette.success.main,
    // Red, so it can't be mistaken for an admin's orange.
    questionChip: theme.palette.error.main,
    // #14161A / #1C1F24
    text: theme.palette.text.primary,
    // #5F646A / #6B7076 / #8A9096
    textMuted: theme.palette.text.secondary,
    // #DADCE0 / #E1E3E7 / #E3E5E8 / #E7E9EC
    border: theme.palette.divider,
    // #F2F3F5 / #F4F5F7
    hoverSurface: theme.palette.action.hover
});

// Semi-rounded throughout - do not increase.
export const chatRadii = {
    chip: '7px',
    action: '8px',
    input: '10px'
};

// Both composer popovers (scope picker and filter menu) share one paper.
export const chatPopoverPaperSx = (theme) => ({
    width: 196,
    padding: '6px',
    border: `1px solid ${chatColors(theme).border}`,
    borderRadius: chatRadii.input,
    boxShadow: '0 6px 24px rgba(15,17,21,0.16)',
    backgroundColor: theme.palette.common.white
});

// One row in either popover.
export const chatMenuItemSx = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: '100%',
    fontSize: 13,
    padding: '9px 10px',
    borderRadius: chatRadii.action,
    color: chatColors(theme).text,
    textAlign: 'left',
    '&:hover': { backgroundColor: chatColors(theme).hoverSurface }
});

// The "ADD THIS CHAT TO A SLIDE" / "SHOW CHATS FROM" heading above the rows.
export const chatMenuLabelSx = (theme) => ({
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.4px',
    color: chatColors(theme).textMuted,
    padding: '9px 10px 6px'
});

// A sender's avatar, on comments and replies alike. Admins are a rounded square
// as well as orange, so they stand out by shape and not by colour alone.
export const chatAvatarSx = (isAdmin) => (theme) => ({
    backgroundColor: isAdmin ? chatColors(theme).adminAvatar : chatColors(theme).audienceAvatar,
    borderRadius: isAdmin ? chatRadii.action : '50%'
});

export const chatSizes = {
    avatar: 38,
    replyAvatar: 28,
    send: 36,
    replySend: 28
};

// The circular send button: 36px in the composer, 28px under a message being
// replied to. One block, because the two were the same declarations twice.
//
// The fill already darkens on hover; what it lacked was a focus ring. That ring
// has to sit outside the button - drawn on the button it would be primary on
// primary, invisible - so it is offset onto the white panel behind, where the
// same navy reads at 13.5:1.
export const chatSendSx = (size) => (theme) => ({
    flex: '0 0 auto',
    width: size,
    height: size,
    color: theme.palette.common.white,
    backgroundColor: chatColors(theme).primary,
    '&:hover': {
        backgroundColor: chatColors(theme).primaryHover,
        boxShadow: '0 2px 6px rgba(15,17,21,0.28)'
    },
    '&:focus-visible, &.Mui-focusVisible': {
        backgroundColor: chatColors(theme).primaryHover,
        ...focusRing(chatColors(theme).primary, { offset: '2px' })
    },
    '&.Mui-disabled': {
        color: theme.palette.common.white,
        backgroundColor: chatColors(theme).primaryDisabled
    }
});
